// frontend/src/components/common/ProfileDialog.js
// Lets any signed-in user (any role) change or remove their own profile photo.
import React, { useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Button,
    Box, Typography, CircularProgress, IconButton,
} from '@mui/material';
import { PhotoCamera, Delete, Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { uploadMyPhoto, removeMyPhoto } from '../../services/endpoints';
import { avatarUrl } from '../../utils/avatar';

const MAX_MB = 5;

const ProfileDialog = ({ open, onClose }) => {
    const { user, updateUser } = useAuth();
    const [busy, setBusy] = useState(false);
    const fileInputRef = useRef(null);

    const pickFile = () => fileInputRef.current?.click();

    const onFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // allow re-picking the same file later
        if (!file) return;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Image must be under ${MAX_MB} MB`);
            return;
        }
        setBusy(true);
        try {
            const res = await uploadMyPhoto(file);
            updateUser(res.data.user);
            toast.success('Profile photo updated');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not upload photo');
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async () => {
        setBusy(true);
        try {
            const res = await removeMyPhoto();
            updateUser(res.data.user);
            toast.success('Profile photo removed');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not remove photo');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Profile
                <IconButton onClick={onClose} size="small"><Close /></IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            src={avatarUrl(user?.avatar_path)}
                            sx={{ width: 96, height: 96, fontSize: 36, bgcolor: 'primary.main' }}
                        >
                            {user?.full_name?.charAt(0) || 'U'}
                        </Avatar>
                        {busy && (
                            <Box sx={{
                                position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                                bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '50%',
                            }}>
                                <CircularProgress size={28} sx={{ color: '#fff' }} />
                            </Box>
                        )}
                    </Box>

                    <Typography variant="subtitle1" fontWeight={700}>{user?.full_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{user?.email}</Typography>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<PhotoCamera />}
                            onClick={pickFile} disabled={busy}>
                            {user?.avatar_path ? 'Change photo' : 'Upload photo'}
                        </Button>
                        {user?.avatar_path && (
                            <Button variant="text" size="small" color="error" startIcon={<Delete />}
                                onClick={handleRemove} disabled={busy}>
                                Remove
                            </Button>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        PNG, JPG, GIF or WEBP, up to {MAX_MB} MB.
                    </Typography>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        hidden
                        onChange={onFileChange}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileDialog;
